import { Changes, ChangeValue, ITicketHistory } from '../types';
import TimeFormatter from '@/app/helpers/TimeFormatter';

export interface EntityRow {
    id: string;
    ticketId: string;
    createdAt: string;
    author: number;
    content?: string;
    changedFields: string[];
    changes: Changes;
}

const detectColumnType = (
    value: unknown
): 'text' | 'number' | 'boolean' | 'date' => {
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'string') {
        if (TimeFormatter.isValidDate(value)) {
            return 'date';
        } else {
            return 'text';
        }
    }
    if (value instanceof Date) return 'date';
    return 'text';
};

const dynamicKeys = (ticketHistory: ITicketHistory[]) => {
    return Array.from(
        new Set(ticketHistory.flatMap((h) => Object.keys(h.changes ?? {})))
    ).filter(
        (key) =>
            !['status', 'name', 'description', 'assignedUser'].includes(key)
    );
};

export const dynamicColumns = (ticketHistory: ITicketHistory[]) => {
    return dynamicKeys(ticketHistory).flatMap((key) => {
        const historyEntry = ticketHistory.find((h) => h.changes?.[key])
            ?.changes?.[key]?.newValue;
        return historyEntry === undefined
            ? []
            : [
                  {
                      id: key,
                      label: key,
                      type: detectColumnType(historyEntry),
                      showAlways: false,
                  },
              ];
    });
};

export function buildEntities(ticketHistory: ITicketHistory[]): EntityRow[] {
    const entities: EntityRow[] = [];
    const state: Record<string, ChangeValue | undefined> = {};

    for (const item of ticketHistory) {
        const snapshot: EntityRow = {
            ...state,
            id: item.id,
            ticketId: item.ticketId,
            createdAt: item.createdAt,
            author: item.author,
            content: item.content,
            changedFields: [],
            changes: item.changes || {},
        };

        if (item.changes) {
            for (const [key, change] of Object.entries(item.changes)) {
                if (change?.newValue !== undefined) {
                    snapshot[key] = change.newValue;
                    state[key] = change.newValue;
                    snapshot.changedFields.push(key);
                } else if (change?.oldValue !== undefined) {
                    snapshot[key] = undefined;
                    state[key] = undefined;
                    snapshot.changedFields.push(key);
                }
            }
        }

        entities.push(snapshot);
    }

    return entities.reverse();
}

const highlightChangedCellStyle = (params: any) => {
    if (params.data?.changedFields?.includes(params.colDef.field)) {
        return { backgroundColor: 'lightgreen' };
    }
    return {};
};

export const withCellStyle = (col: any) => ({
    ...col,
    cellStyle: highlightChangedCellStyle,
});
